import { FiSearch, FiAlertCircle, FiEye, FiEyeOff, FiCalendar, FiMapPin, FiUser, FiInfo } from "react-icons/fi";
import { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { makeRequestAuth } from "../../_config/api";

function AllProperties() {
  const [properties, setProperties] = useState([]);
  const [pageNo, setPageNo] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});

    const [confirmModal, setConfirmModal] = useState({
    open: false,
    propertyId: null,
    targetStatus: "",
  });


  const pageSize = 10;
  const maxVisiblePages = 5;

  const [filters, setFilters] = useState({ keyword: "" });
  const [propertiesType, setPropertiesType] = useState("All");

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const fetchData = async () => {
    try {
      const resData = {
        keyWord: filters.keyword,
        status: "rejected",
        isDisable: false,
        sortOrder: "desc",
        sortBy: "createdAt",
        pageNo,
        size: pageSize,
        propertyType: propertiesType === "All" ? "" : propertiesType.toLowerCase(),
      };

      const response = await makeRequestAuth("post", "/admin/getPropertyList", resData);
      setProperties(response.data.list || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      showErrorToast("Failed to fetch rejected properties.");
    }
  };

  useEffect(() => { fetchData(); }, [pageNo, filters, propertiesType]);

    const onStatusSelect = (id, newStatus) => {
    if (newStatus === "approved" || newStatus === "pending") {
      setConfirmModal({
        open: true,
        propertyId: id,
        targetStatus: newStatus,
      });
    } else {
      handleStatusChange(id, newStatus);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await makeRequestAuth("post", "/admin/updatePropertyStatus", {
        propertyId: id,
        status: newStatus,
      });
      showSuccessToast("Status updated successfully!");
      fetchData();
    } catch (error) {
      showErrorToast("Failed to update status.");
    }
  };

  const submitStatusChange = async () => {
    await handleStatusChange(confirmModal.propertyId, confirmModal.targetStatus);
    setConfirmModal({ open: false, propertyId: null, targetStatus: "" });
  };


  

  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalCount / pageSize);
    const start = Math.max(1, pageNo - Math.floor(maxVisiblePages / 2));
    const end = Math.min(start + maxVisiblePages - 1, totalPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="p-4 md:p-8 bg-slate-50 min-h-screen font-sans text-slate-900">
      <ToastContainer position="top-right" />

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl shadow-sm border border-slate-200/60">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-800 flex items-center gap-3">
              <span className="p-2 bg-red-100 text-red-600 rounded-xl">
                <FiAlertCircle size={24} />
              </span>
              Rejected Listings
            </h1>
            <p className="text-slate-500 text-sm font-medium mt-1 ml-12">
              Viewing {totalCount} properties that need review or correction.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative group">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="text"
                placeholder="Search owner or ID..."
                className="pl-10 pr-4 py-2.5 bg-slate-100 border-transparent rounded-2xl focus:bg-white focus:ring-2 focus:blue-red-500/20 focus:border-[#24ADD7] outline-none transition-all w-full md:w-64 text-sm"
                onChange={(e) => { setFilters({ ...filters, keyword: e.target.value }); setPageNo(1); }}
              />
            </div>
            <select
              className="bg-slate-100 border-transparent rounded-2xl px-4 py-2.5 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer"
              value={propertiesType}
              onChange={(e) => { setPropertiesType(e.target.value); setPageNo(1); }}
            >
              <option>All Types</option>
              <option>Apartment</option>
              <option>House</option>
              <option>Commercial</option>
            </select>
          </div>
        </div>

        {/* List Section */}
        <div className="space-y-4">
          {properties.length > 0 ? properties.map((property) => (
            <div key={property._id} className="bg-white rounded-[2rem] border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group">
              <div className="flex flex-col lg:flex-row p-4 md:p-6 gap-6">

                {/* Image Section */}
                <div className="relative w-full lg:w-64 h-48 flex-shrink-0 rounded-2xl overflow-hidden shadow-inner bg-slate-100">
                  <img
                    src={property?.uploadedPhotos?.[0] || "https://via.placeholder.com/400x300?text=No+Image"}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    alt="property"
                  />
                  <div className="absolute top-3 left-3 px-3 py-1 bg-white/90 backdrop-blur-md rounded-full shadow-sm">
                    <span className="text-[10px] font-black uppercase text-red-600 tracking-tighter italic">Rejected</span>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-grow flex flex-col justify-between py-1">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold rounded uppercase">{property?.propertyType || "Property"}</span>
                          <span className="text-slate-300">•</span>
                          <span className="text-slate-400 text-xs font-mono tracking-tighter">ID: {property?._id?.slice(-8)}</span>
                        </div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                          {property?.uploadBy?.name || "Unknown Owner"}
                        </h2>
                      </div>

                      <div className="text-right">
                        <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">Pricing</p>
                        <p className="text-lg font-black text-slate-900 tracking-tight">₹{property?.price?.toLocaleString() || "N/A"}</p>
                      </div>
                    </div>

                    {/* Rejection Highlight - Main Card */}
                    <div className="bg-[#E4F3F7] border border-blue-300 p-3 rounded-2xl flex items-start gap-3">
                      <div className="mt-0.5 p-1 bg-red-100 text-red-600 rounded-lg">
                        <FiInfo size={14} />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase text-red-400 tracking-wider">Rejection Reason</p>
                        <p className="text-sm text-red-700 font-medium line-clamp-2">
                          {property?.rejectedReason || "Reason not specified by the admin."}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-y-2 gap-x-6">
                      <div className="flex items-center gap-2 text-slate-500">
                        <FiMapPin className="text-[#24ADD7]" />
                        <span className="text-sm font-semibold">{property?.city || "N/A"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-500">
                        <FiCalendar className="text-[#24ADD7]" />
                        <span className="text-sm font-semibold">{new Date(property.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions Section */}
                <div className="flex flex-col gap-3 lg:w-48 lg:border-l border-slate-100 lg:pl-6 justify-center">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Change Status</label>
                    <select
                      value={property.status}
onChange={(e) => onStatusSelect(property?._id, e.target.value)}
                      className="w-full px-4 py-2.5 rounded-2xl border-2 border-slate-100 text-xs font-bold focus:border-[#24ADD7] outline-none cursor-pointer bg-slate-50 hover:bg-white transition-all"
                    >
                      <option value="rejected">Rejected</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  <button
                    onClick={() => toggleRow(property._id)}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-2xl text-xs font-black uppercase tracking-tighter transition-all shadow-sm ${expandedRows[property._id]
                      ? "bg-slate-800 text-white"
                      : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
                      }`}
                  >
                    {expandedRows[property._id] ? <><FiEyeOff /> Close</> : <><FiEye /> Details</>}
                  </button>
                </div>
              </div>

              {/* Expandable Details Area */}
              {expandedRows[property._id] && (
                <div className="bg-slate-50/80 border-t border-slate-100 p-6 md:p-8 animate-in slide-in-from-top-4 duration-500">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                    <div className="space-y-6">
                      <h4 className="text-sm font-black text-slate-800 uppercase border-b border-slate-200 pb-2">Contact Info</h4>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-slate-400 border border-slate-200"><FiUser size={14} /></div>
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase">Owner Name</p>
                            <p className="text-sm font-bold text-slate-700">{property.uploadBy?.name}</p>
                          </div>
                        </div>
                        <div className="ml-11">
                          <p className="text-[10px] font-bold text-slate-400 uppercase">Email Address</p>
                          <p className="text-sm font-medium text-slate-600">{property.uploadBy?.email}</p>
                        </div>
                        <div className="ml-11">
                          <p className="text-[10px] font-bold text-slate-400 uppercase">Contact Number</p>
                          <p className="text-sm font-medium text-slate-600">{property.uploadBy?.phone}</p>
                        </div>
                      </div>
                    </div>

                    <div className="md:col-span-2 space-y-6">
                      <h4 className="text-sm font-black text-slate-800 uppercase border-b border-slate-200 pb-2">Property Description & Address</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white p-5 rounded-3xl border border-slate-200/50 shadow-sm">
                        <div className="col-span-2">
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Full Address</p>
                          <p className="text-sm text-slate-600 leading-relaxed font-medium italic">"{property.propertyAddress}"</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">City / Locality</p>
                          <p className="text-sm font-bold text-slate-700">{property.city} / {property.localityArea || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Built-up Area</p>
                          <p className="text-sm font-bold text-slate-700">{property.area || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h4 className="text-sm font-black text-slate-800 uppercase mb-4">Verification Gallery</h4>
                    <div className="flex flex-wrap gap-4">
                      {property.uploadedPhotos?.map((url, i) => (
                        <div key={i} className="group/img relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-white shadow-md cursor-zoom-in">
                          <img src={url} className="w-full h-full object-cover group-hover/img:scale-125 transition-transform duration-500" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )) : (
            <div className="py-20 text-center bg-white rounded-[2rem] border border-dashed border-slate-300">
              <FiAlertCircle className="mx-auto text-slate-300 mb-4" size={48} />
              <p className="text-slate-500 font-bold uppercase tracking-widest">No Rejected Properties Found</p>
            </div>
          )}
        </div>

        {/* Pagination Section */}
        <div className="flex flex-col sm:flex-row items-center justify-between bg-white p-4 rounded-3xl shadow-sm border border-blue-300/60">
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
            Showing <span className="text-slate-900">{Math.min(pageNo * pageSize, totalCount)}</span> of {totalCount} Records
          </p>

          <div className="flex items-center gap-2">
            <button
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-30 hover:bg-blue-50 hover:text-[#24ADD7] transition-all"
              onClick={() => setPageNo(p => Math.max(p - 1, 1))}
              disabled={pageNo === 1}
            >
              &larr;
            </button>

            <div className="flex gap-1">
              {getPageNumbers().map(p => (
                <button
                  key={p}
                  onClick={() => setPageNo(p)}
                  className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${p === pageNo
                    ? "bg-[#24ADD7] text-white shadow-lg shadow-blue-300 scale-110"
                    : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                >
                  {p}
                </button>
              ))}
                    {/* Confirmation Modal for Approved / Pending */}
      {confirmModal.open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-[90%] max-w-md shadow-2xl space-y-4 animate-in fade-in zoom-in duration-200">
            <h3 className="text-lg font-bold text-gray-800">
              {confirmModal.targetStatus === "approved"
                ? "Confirm Property Approval"
                : "Confirm Move to Pending"}
            </h3>

            <p className="text-sm text-gray-600 leading-relaxed">
              {confirmModal.targetStatus === "approved"
                ? "Are you sure you want to approve this property? Once approved, it will be listed live on the platform."
                : "Are you sure you want to move this property back to pending status for re-review?"}
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setConfirmModal({ open: false, propertyId: null, targetStatus: "" })}
                className="px-4 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>

              <button
                onClick={submitStatusChange}
                className={`px-5 py-2 text-white rounded-xl text-sm font-bold transition-all shadow-md ${
                  confirmModal.targetStatus === "approved"
                    ? "bg-green-600 hover:bg-green-700 shadow-green-100"
                    : "bg-amber-500 hover:bg-amber-600 shadow-amber-100"
                }`}
              >
                {confirmModal.targetStatus === "approved"
                  ? "Confirm Approve"
                  : "Move to Pending"}
              </button>
            </div>
          </div>
        </div>
      )}

            </div>

            <button
              className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-600 disabled:opacity-30 hover:bg-blue-50 hover:text-[#24ADD7] transition-all"
              onClick={() => setPageNo(p => Math.min(p + 1, totalPages))}
              disabled={pageNo === totalPages}
            >
              &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AllProperties;